import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { Chessboard } from 'react-chessboard';
import type { Move, Square, Promotion } from '../../domain/chess/ChessRules';
import { palette } from '../../design/assets';
import { boardImage, imageSrc, pieces, pieceImage } from './pieces';
import {
  ARROW_COLOR,
  uniqueArrows,
  MARK_COLOR,
  toggleArrow,
  toggleSquare,
  useBoardDrawing,
  type Marks,
} from './annotations';
interface Props {
  fen: string;
  orientation: 'white' | 'black';
  enabled: boolean;
  legalMoves: Move[];
  lastMove?: Move;
  moveTone?: 'normal' | 'mistake' | 'defense';
  checkSquare?: Square;
  hint?: string;
  hintLevel: number;
  coordinates: boolean;
  reduced: boolean;
  /** R5: the opponent's setup move slides a little slower than other moves. */
  animationMs?: number;
  onMove: (uci: string) => boolean;
  /** R5: a touch hold turned into drawing (the app plays a light haptic tap). */
  onHold?: () => void;
  /** R5: Pen tool on: taps and drags draw; pieces don't move. */
  pen?: boolean;
  /** R5: press and hold on touch draws (Settings; on by default). */
  holdToDraw?: boolean;
  /** R5: change this number to clear every highlight and arrow. */
  clearToken?: number;
  /** R5: how many highlights and arrows are on the board now. */
  onAnnotations?: (count: number) => void;
}
export function BoardAdapter(p: Props) {
  const board = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(p.orientation === 'white' ? 'a1' : 'h8');
  useLayoutEffect(() => {
    const element = board.current;
    if (!element) return;
    // Squares own keyboard input and announce their pieces. The library's nested
    // drag buttons keep their pointer handlers, without duplicate focus/AT targets.
    const normalize = () => {
      element
        .querySelectorAll<HTMLElement>('[aria-roledescription="draggable"]')
        .forEach((piece) => {
          piece.removeAttribute('role');
          piece.removeAttribute('tabindex');
          piece.setAttribute('aria-hidden', 'true');
        });
    };
    normalize();
    const observer = new MutationObserver(normalize);
    observer.observe(element, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  const [selection, setSelection] = useState<{ square: string; fen: string }>();
  const selected = p.enabled && selection?.fen === p.fen ? selection.square : undefined;
  if (selection && (selection.fen !== p.fen || !p.enabled)) setSelection(undefined);
  const [promotion, setPromotion] = useState<{ from: string; to: string; fen: string }>();
  const promotionDialog = useRef<HTMLDialogElement>(null);
  if (promotion && (promotion.fen !== p.fen || !p.enabled)) setPromotion(undefined);
  useLayoutEffect(() => {
    const dialog = promotionDialog.current;
    if (!promotion || !dialog) return;
    const place = () => {
      const anchor = board.current?.getBoundingClientRect();
      if (!anchor) return;
      const bounds = dialog.getBoundingClientRect();
      const x = Math.max(
        bounds.width / 2 + 12,
        Math.min(window.innerWidth - bounds.width / 2 - 12, anchor.x + anchor.width / 2),
      );
      const y = Math.max(
        bounds.height / 2 + 12,
        Math.min(window.innerHeight - bounds.height / 2 - 12, anchor.y + anchor.height / 2),
      );
      dialog.style.left = `${x}px`;
      dialog.style.top = `${y}px`;
    };
    dialog.showModal();
    place();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : undefined;
    if (board.current) observer?.observe(board.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      observer?.disconnect();
      dialog.close();
    };
  }, [promotion]);
  const cancelPromotion = () => {
    promotionDialog.current?.close();
    setPromotion(undefined);
    board.current?.querySelector<HTMLElement>(`[data-board-square="${promotion?.from}"]`)?.focus();
  };
  // R5 annotations: every highlight and arrow lives here. They stay until Clear,
  // a move (a new position starts clean), or the same gesture again.
  const [marks, setMarks] = useState<Marks>({ fen: p.fen, squares: [], arrows: [] });
  const current = marks.fen === p.fen ? marks : { fen: p.fen, squares: [], arrows: [] };
  const drawing = useBoardDrawing({
    pen: !!p.pen,
    holdToDraw: p.holdToDraw ?? true,
    onHold: () => p.onHold?.(),
    onDraw: (from, to) =>
      setMarks(from === to ? toggleSquare(current, from) : toggleArrow(current, from, to)),
  });
  const cancelDrawing = drawing.cancel;
  useLayoutEffect(() => {
    cancelDrawing();
  }, [cancelDrawing, p.fen, p.orientation, p.pen, p.holdToDraw, p.enabled, p.clearToken]);
  const [clearToken, setClearToken] = useState(p.clearToken);
  if (clearToken !== p.clearToken) {
    setClearToken(p.clearToken);
    setMarks({ fen: p.fen, squares: [], arrows: [] });
  }
  const annotationCount = current.squares.length + current.arrows.length;
  const { onAnnotations } = p;
  useEffect(() => {
    onAnnotations?.(annotationCount);
  }, [onAnnotations, annotationCount]);
  // Each arrow once: the board keys arrows by their squares, and a duplicate (an
  // arrow being redrawn, or one matching the hint) left a stale arrow on screen.
  const shownArrows = uniqueArrows([
    ...(p.hint && p.hintLevel >= 2
      ? [
          {
            startSquare: p.hint.slice(0, 2),
            endSquare: p.hint.slice(2, 4),
            color: '#d1a34acc',
          },
        ]
      : p.lastMove && p.moveTone === 'defense'
        ? [{ startSquare: p.lastMove.from, endSquare: p.lastMove.to, color: '#e8af42d9' }]
        : []),
    ...current.arrows,
    ...(drawing.preview && drawing.preview.from !== drawing.preview.to
      ? [
          {
            startSquare: drawing.preview.from,
            endSquare: drawing.preview.to,
            color: ARROW_COLOR,
          },
        ]
      : []),
  ]);
  const styles: Record<string, CSSProperties> = {};
  if (p.lastMove)
    for (const square of [p.lastMove.from, p.lastMove.to])
      styles[square] = { backgroundColor: p.moveTone === 'mistake' ? '#e16858a6' : '#f6eb6199' };
  for (const square of current.squares) styles[square] = { backgroundColor: MARK_COLOR };
  if (drawing.preview)
    styles[drawing.preview.from] = {
      ...styles[drawing.preview.from],
      boxShadow: `inset 0 0 0 4px ${ARROW_COLOR}`,
    };
  if (selected) {
    styles[selected] = { backgroundColor: '#f6eb6199' };
    for (const m of p.legalMoves.filter((m) => m.from === selected))
      styles[m.to] = {
        ...styles[m.to],
        backgroundImage: m.captured
          ? 'radial-gradient(transparent 56%, #20232966 58%, #20232966 69%, transparent 71%)'
          : 'radial-gradient(circle, #20232959 22%, transparent 24%)',
      };
  }
  if (p.checkSquare)
    styles[p.checkSquare] = {
      ...styles[p.checkSquare],
      backgroundImage: 'radial-gradient(#ce6b5ecc, transparent 75%)',
    };
  if (p.hint && p.hintLevel > 0)
    styles[p.hint.slice(0, 2)] = {
      ...styles[p.hint.slice(0, 2)],
      boxShadow: 'inset 0 0 0 5px #d1a34a',
    };
  const attempt = (from: string, to: string) => {
    if (!p.enabled) return false;
    if (p.legalMoves.some((m) => m.from === from && m.to === to && m.promotion)) {
      setPromotion({ from, to, fen: p.fen });
      setSelection(undefined);
      return false;
    }
    setSelection(undefined);
    return p.onMove(from + to);
  };
  const select = (square: string, keyboard = false) => {
    // With the Pen on, or for the press that just drew, a tap selects nothing.
    if (!keyboard && (p.pen || drawing.quiet())) return;
    if (!p.enabled) return;
    if (selected && p.legalMoves.some((m) => m.from === selected && m.to === square)) {
      attempt(selected, square);
      return;
    }
    setSelection(
      selected !== square && p.legalMoves.some((m) => m.from === square)
        ? { square, fen: p.fen }
        : undefined,
    );
  };
  const keyboard = (e: KeyboardEvent<HTMLDivElement>, square: string) => {
    if (e.key === 'Escape' && selected) {
      e.preventDefault();
      e.stopPropagation();
      setSelection(undefined);
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select(square, true);
    }
    const directions: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, 1],
      ArrowDown: [0, -1],
    };
    if (directions[e.key]) {
      e.preventDefault();
      const d = directions[e.key],
        sign = p.orientation === 'white' ? 1 : -1;
      const file = square.charCodeAt(0) + d[0] * sign,
        rank = Number(square[1]) + d[1] * sign;
      if (file >= 97 && file <= 104 && rank >= 1 && rank <= 8)
        board.current
          ?.querySelector<HTMLElement>(`[data-board-square="${String.fromCharCode(file)}${rank}"]`)
          ?.focus();
    }
  };
  return (
    <div
      className="board-wrap"
      ref={board}
      role="group"
      data-testid="board"
      data-fen={p.fen}
      data-orientation={p.orientation}
      aria-label={`Chessboard, ${p.fen.split(' ')[1] === 'w' ? 'White' : 'Black'} to move`}
      data-move-tone={p.moveTone ?? 'normal'}
      data-annotations={annotationCount}
      data-pen={p.pen || undefined}
      onPointerDownCapture={drawing.onPointerDownCapture}
      // The board never opens the browser menu: right-click is for drawing.
      onContextMenu={(e) => e.preventDefault()}
    >
      <Chessboard
        options={{
          id: 'aftermove-board',
          position: p.fen,
          boardOrientation: p.orientation,
          pieces,
          allowDragging: p.enabled,
          allowDragOffBoard: false,
          allowAutoScroll: false,
          dragActivationDistance: 3,
          // Drawing is ours (see annotations.ts); the library only renders the arrows.
          allowDrawingArrows: false,
          animationDurationInMs: p.reduced ? 0 : (p.animationMs ?? 140),
          showAnimations: !p.reduced,
          showNotation: p.coordinates,
          boardStyle: {
            borderRadius: '5px',
            overflow: 'hidden',
            backgroundImage: `url("${imageSrc(boardImage)}")`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
          },
          darkSquareStyle: { backgroundColor: 'transparent' },
          lightSquareStyle: { backgroundColor: 'transparent' },
          darkSquareNotationStyle: { color: palette.parchment, fontSize: '11px' },
          lightSquareNotationStyle: { color: '#4c6831', fontSize: '11px' },
          arrows: shownArrows,
          canDragPiece: ({ square }) =>
            !p.pen && !!square && p.legalMoves.some((m) => m.from === square),
          onPieceDrag: ({ square }) => {
            drawing.cancelPending();
            if (square && p.enabled) setSelection({ square, fen: p.fen });
          },
          onPieceDragCancel: () => setSelection(undefined),
          onPieceDrop: ({ sourceSquare, targetSquare }) =>
            targetSquare ? attempt(sourceSquare, targetSquare) : false,
          onSquareClick: ({ square }) => select(square),
          squareRenderer: ({ square, piece, children }) => (
            <div
              data-board-square={square}
              data-last-move={
                p.lastMove?.from === square ? 'from' : p.lastMove?.to === square ? 'to' : undefined
              }
              data-marked={current.squares.includes(square) || undefined}
              role="button"
              tabIndex={focused === square ? 0 : -1}
              onFocus={(e) => {
                setFocused(square);
                if (e.target !== e.currentTarget) e.currentTarget.focus();
              }}
              aria-label={`${square}${piece ? ` ${piece.pieceType[0] === 'w' ? 'White' : 'Black'} ${({ P: 'pawn', N: 'knight', B: 'bishop', R: 'rook', Q: 'queen', K: 'king' } as Record<string, string>)[piece.pieceType[1]]}` : ', empty'}`}
              aria-disabled={!p.enabled}
              aria-pressed={selected === square}
              className="square-access"
              // A custom renderer replaces the library's squareStyles wrapper.
              style={styles[square]}
              onKeyDown={(e) => keyboard(e, square)}
            >
              {children}
            </div>
          ),
        }}
      />
      {promotion && promotion.fen === p.fen && p.enabled && (
        <dialog
          ref={promotionDialog}
          aria-modal="true"
          aria-label="Choose promotion"
          className="promotion"
          onKeyDown={(e) => {
            if (e.key !== 'Tab') return;
            const buttons = e.currentTarget.querySelectorAll<HTMLButtonElement>('button');
            const first = buttons[0],
              last = buttons[buttons.length - 1];
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last?.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first?.focus();
            }
          }}
          onCancel={(e) => {
            e.preventDefault();
            cancelPromotion();
          }}
        >
          <h2>Promote your pawn</h2>
          <div>
            {(['q', 'r', 'b', 'n'] as Promotion[]).map((piece, i) => (
              <button
                key={piece}
                autoFocus={i === 0}
                onClick={() => {
                  promotionDialog.current?.close();
                  p.onMove(promotion.from + promotion.to + piece);
                  setPromotion(undefined);
                }}
              >
                <img
                  src={imageSrc(pieceImage(`${p.legalMoves[0]?.color ?? 'w'}${piece}`))}
                  data-src={pieceImage(`${p.legalMoves[0]?.color ?? 'w'}${piece}`)}
                  alt=""
                  width={48}
                  height={48}
                />
                {{ q: 'Queen', r: 'Rook', b: 'Bishop', n: 'Knight' }[piece]}
              </button>
            ))}
          </div>
          <button onClick={cancelPromotion}>Cancel</button>
        </dialog>
      )}
    </div>
  );
}
